import { t } from '../../strings';
import type { IconName } from './icons';
import { el, ensureStyle } from '../dom';
import { icon } from './icons';
import { focusLabel, focusToken, narrow, type Focus } from '../../core/quiz/focus';
import { studyCodes } from '../../core/ignored';
import { soundPool } from '../../core/quiz/sound';
import { photoPool } from '../../core/quiz/photo';
import { soundMatchPool } from '../../core/quiz/sound-match';
import { photosReady } from '../../core/media/image';

// "Study these nine" — which is a question with five answers, so it is a menu.
//
// A focus says which birds; it says nothing about which sense. Sitting down
// with the flycatchers might mean their songs, or their silhouettes, or all of
// it at once, and there is no defensible default among the five — so the button
// asks rather than guesses. Once picked, the round is that mode's ordinary
// round over a smaller pool: same setup screen, same options, same grading.
//
// Every row carries its own count, and that is the point of the menu rather
// than an ornament on it. Nine sparrows is nine questions in Name Match and
// seven in Photo ID if two of them have no photograph, and a mode that can ask
// nothing at all says so instead of being pressed and then apologising.
//
// The popover mechanics — scrim, viewport clamp, Escape, click-outside, close
// on any navigation — are the species picker's, deliberately: this app should
// have one kind of menu, not two.

ensureStyle(
  'mode-menu-css',
  `
  .mmenu-scrim { position:fixed; inset:0; z-index:60; background:rgba(0,0,0,.18); }
  .mmenu {
    position:fixed; z-index:61; display:flex; flex-direction:column;
    background:var(--card); color:var(--ink);
    border:1.5px solid var(--hairline-strong); border-radius:var(--radius-lg);
    box-shadow:var(--shadow); overflow:hidden; padding:4px 0;
  }
  .mmenu .mmenu-head { padding:9px 13px 7px; color:var(--ink-soft);
    border-bottom:1.5px solid var(--hairline); margin-bottom:4px; }
  .mmenu .mmenu-row { display:flex; align-items:center; gap:11px; width:100%;
    padding:9px 13px; background:none; border:none; text-align:left;
    color:inherit; }
  .mmenu .mmenu-row:hover:not(:disabled), .mmenu .mmenu-row.on { background:var(--sunk); }
  .mmenu .mmenu-row .icon { flex:none; opacity:.85; }
  .mmenu .mmenu-row .mode { flex:1; font-family:var(--font-display); font-size:15.5px;
    line-height:1.15; }
  /* Monospaced so the counts line up down the column and the menu reads as a
     table of what is available rather than as five sentences. */
  .mmenu .mmenu-row .n { flex:none; font-family:var(--font-mono); font-size:11.5px;
    color:var(--ink-soft); }
  /* A mode with nothing to ask stays on the list rather than vanishing: which
     modes exist is a fact about the app, and which of them this focus can feed
     is the thing worth showing. */
  .mmenu .mmenu-row:disabled { opacity:.42; }
`,
);

/** Narrower than this and a label wraps; the popover shifts sideways instead. */
const MIN_WIDTH = 226;

/** Breathing room between the popover and the edge of the screen. */
const EDGE = 10;

interface Mode {
  route: string;
  glyph: IconName;
  label: string;
  /** The species this mode can ask about, before the focus narrows them. */
  pool: () => string[];
  /** Whether the mode needs the downloaded media pack to ask anything. */
  needsPack?: boolean;
}

/**
 * The five rounds, in the order the Home grid lists them, with the icons and
 * the names it uses. Two screens naming the same thing differently is how a
 * learner ends up believing there are six modes.
 */
const MODES: readonly Mode[] = [
  { route: '/quiz/field', glyph: 'binoculars', label: t('common.mode.field'), pool: studyCodes },
  { route: '/quiz/sound', glyph: 'music', label: t('common.mode.sound'), pool: () => soundPool() },
  { route: '/quiz/names', glyph: 'tag', label: t('common.mode.names'), pool: studyCodes },
  { route: '/quiz/photo', glyph: 'eye', label: t('common.mode.photo'), pool: photoPool, needsPack: true },
  {
    route: '/quiz/sound-match',
    glyph: 'waveform',
    label: t('common.mode.soundMatch'),
    pool: () => soundMatchPool(),
    needsPack: true,
  },
];

export interface ModeMenuOptions {
  focus: Focus;
  /** The birds the focus comes to, already resolved by the caller. */
  codes: readonly string[];
  navigate: (path: string) => void;
}

/**
 * Open the menu under `near`.
 *
 * Resolves nothing: the choice arrives as a navigation, and dismissal is
 * silent, which is what a menu closing means.
 */
export function openModeMenu(near: HTMLElement, opts: ModeMenuOptions): void {
  // Whether the pack is on this device decides two of the five rows, and it is
  // the one thing here that has to be waited for. Asked once, up front, rather
  // than once per row.
  void photosReady().then(build);

  function build(withPhotos: boolean): void {
    const rows: HTMLButtonElement[] = [];
    const panel = el(
      'div.mmenu',
      { role: 'menu', 'aria-label': t('common.modeMenu.label') },
      el('div.mmenu-head.label', {}, focusLabel(opts.focus, opts.codes.length)),
    );
    const scrim = el('div.mmenu-scrim', {});

    for (const mode of MODES) {
      const n = narrow(mode.pool(), opts.codes).length;
      // A mode the pack has not arrived for would deal a round of placeholders,
      // which is the same unanswerable round Photo ID already refuses to start.
      const blocked = Boolean(mode.needsPack) && !withPhotos;
      const row = el('button.mmenu-row', {
        type: 'button',
        role: 'menuitem',
        title: blocked
          ? t('common.modeMenu.needsPack')
          : n === 0
            ? t('common.modeMenu.noneAskable', { mode: mode.label })
            : t('common.modeMenu.count', { n, mode: mode.label }),
      }) as HTMLButtonElement;
      row.disabled = blocked || n === 0;
      row.append(
        icon(mode.glyph, { size: 18 }),
        el('span.mode', {}, mode.label),
        el('span.n', {}, blocked ? t('common.modeMenu.noPack') : String(n)),
      );
      row.addEventListener('click', () => {
        close();
        opts.navigate(`${mode.route}?focus=${focusToken(opts.focus)}`);
      });
      panel.append(row);
      rows.push(row);
    }

    /** The row the keyboard is on, among the ones that can actually be pressed. */
    let cursor = -1;
    const live = (): HTMLButtonElement[] => rows.filter((r) => !r.disabled);

    const paintCursor = (): void => {
      for (const r of rows) r.classList.remove('on');
      const at = live()[cursor];
      if (at) at.classList.add('on');
    };

    let closed = false;
    function close(): void {
      if (closed) return;
      closed = true;
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('resize', place);
      window.removeEventListener('hashchange', close);
      scrim.remove();
      panel.remove();
      // Back to whatever opened the menu, so a keyboard user is not dropped at
      // the top of the document.
      near.focus?.();
    }

    function onKey(e: KeyboardEvent): void {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        close();
        return;
      }
      // A menu that is up owns all four arrows, whether it uses them or not —
      // the Library scrolls under this one, and a list moving beneath an open
      // menu is how you end up starting a round you did not choose.
      if (e.key.startsWith('Arrow')) {
        e.preventDefault();
        e.stopPropagation();
        if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
        const shown = live();
        if (!shown.length) return;
        cursor = (cursor + (e.key === 'ArrowDown' ? 1 : -1) + shown.length) % shown.length;
        paintCursor();
        return;
      }
      if (e.key === 'Enter') {
        const at = live()[cursor];
        if (!at) return;
        e.preventDefault();
        // Stopped as well as prevented: the screen behind this menu has an
        // Enter of its own, and choosing a mode must not also press it.
        e.stopPropagation();
        at.click();
      }
    }

    /** Put the panel under its trigger, then drag it back inside the screen. */
    function place(): void {
      const box = near.getBoundingClientRect();
      const width = Math.min(Math.max(box.width, MIN_WIDTH), window.innerWidth - EDGE * 2);
      panel.style.width = `${width}px`;

      let left = box.left;
      if (left + width > window.innerWidth - EDGE) left = window.innerWidth - EDGE - width;
      if (left < EDGE) left = EDGE;
      panel.style.left = `${Math.round(left)}px`;

      // Below by preference, above when there is more room up there — a family
      // heading near the foot of the screen would otherwise get a clipped menu.
      const height = panel.offsetHeight || 240;
      const below = window.innerHeight - box.bottom - EDGE;
      if (below >= height || below >= box.top - EDGE) {
        panel.style.top = `${Math.round(box.bottom + 6)}px`;
        panel.style.bottom = 'auto';
      } else {
        panel.style.bottom = `${Math.round(window.innerHeight - box.top + 6)}px`;
        panel.style.top = 'auto';
      }
    }

    scrim.addEventListener('click', close);
    // Capture, so Escape closes the menu rather than reaching the screen behind
    // it, which has an Escape of its own.
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('resize', place);
    // Any navigation at all leaves the menu orphaned over a screen it was never
    // opened from.
    window.addEventListener('hashchange', close);

    document.body.append(scrim, panel);
    place();
    // Opens on the first row that can be pressed, so Enter is immediately
    // useful and a screen reader lands inside the menu rather than behind it.
    cursor = live().length ? 0 : -1;
    paintCursor();
    (live()[0] ?? panel).focus?.();
  }
}
